'use client';

import Script from 'next/script';
import { useEffect } from 'react';

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
}

export default function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  useEffect(() => {
    // Initialize dataLayer if it doesn't exist
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  return (
    <>
      {/* Google Analytics Script */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics-config"
        strategy="afterInteractive"
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            page_title: document.title,
            page_location: window.location.href,
            send_page_view: true
          });
        `}
      </Script>
    </>
  );
}

// Google Analytics tracker utility class
export class GoogleAnalyticsTracker {
  private measurementId: string;

  constructor(measurementId: string) {
    this.measurementId = measurementId;
  }

  // Track page views
  static trackPageView(url: string, title?: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '', {
        page_path: url,
        page_title: title || document.title,
        page_location: window.location.href,
      });
    }
  }

  // Track custom events
  static trackEvent(eventName: string, parameters?: Record<string, any>) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        send_to: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '',
      });
    }
  }



  // Track location interactions
  static trackLocationClick(locationData: {
    locationId: string;
    locationName: string;
    position: number;
    clickType: 'name' | 'image' | 'description' | 'learn_more' | 'view_menu';
  }) {
    this.trackEvent('location_interaction', {
      event_category: 'Location',
      event_label: locationData.clickType,
      location_id: locationData.locationId,
      location_name: locationData.locationName,
      position: locationData.position,
      click_type: locationData.clickType,
    });
  }

  // Track search and discovery
  static trackGlutenFreeSearch(searchData: {
    searchTerm?: string;
    filterType?: string;
    resultsCount?: number;
  }) {
    this.trackEvent('gluten_free_search', {
      event_category: 'Search',
      search_term: searchData.searchTerm,
      filter_type: searchData.filterType,
      results_count: searchData.resultsCount,
    });
  }
}
